import {
  Hand,
  Smartphone,
  Link as LinkIcon
} from "lucide-react";

import { motion } from "framer-motion";

const HowItWorks = () => {

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.75,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const stepsContainer = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const stepAnimation = {
    hidden: {
      opacity: 0,
      y: 45,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  const iconAnimation = {
    hidden: {
      opacity: 0,
      scale: 0.75,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1],
      },
    },
  };

  return (
    <section id="how-it-works" style={howStyles.container}>

      {/* HEADER */}
      <motion.div
        style={howStyles.header}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.15,
        }}
      >

        <motion.p
          style={howStyles.eyebrow}
          variants={fadeUp}
        >
          HOW IT WORKS
        </motion.p>

        <motion.h2
          style={howStyles.title}
          variants={fadeUp}
        >
          One tap.
          <br />
          <span style={howStyles.titleLight}>
            That's it.
          </span>
        </motion.h2>

        <motion.p
          style={howStyles.intro}
          variants={fadeUp}
        >
          Your business is always with you. Just wear your ClusterClear
          wristband and tap to share.
        </motion.p>

      </motion.div>


      {/* STEPS */}
      <motion.div
        style={howStyles.steps}
        variants={stepsContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.15,
        }}
      >

        {/* STEP 01 */}
        <motion.div
          style={howStyles.step}
          variants={stepAnimation}
        >

          <div style={howStyles.number}>
            01
          </div>

          <motion.div
            style={howStyles.iconWrapper}
            variants={iconAnimation}
          >
            <Hand
              size={30}
              strokeWidth={1.6}
            />
          </motion.div>

          <h3 style={howStyles.stepTitle}>
            Wear it
          </h3>

          <p style={howStyles.description}>
            Your business goes wherever you go.
            No phone, battery or charging required.
          </p>

        </motion.div>


        {/* STEP 02 */}
        <motion.div
          style={howStyles.step}
          variants={stepAnimation}
        >

          <div style={howStyles.number}>
            02
          </div>

          <motion.div
            style={howStyles.iconWrapper}
            variants={iconAnimation}
          >
            <Smartphone
              size={30}
              strokeWidth={1.6}
            />
          </motion.div>

          <h3 style={howStyles.stepTitle}>
            Tap it
          </h3>

          <p style={howStyles.description}>
            Someone taps your wristband with
            their phone.
          </p>

        </motion.div>


        {/* STEP 03 */}
        <motion.div
          style={howStyles.step}
          variants={stepAnimation}
        >

          <div style={howStyles.number}>
            03
          </div>

          <motion.div
            style={howStyles.iconWrapper}
            variants={iconAnimation}
          >
            <LinkIcon
              size={30}
              strokeWidth={1.6}
            />
          </motion.div>

          <h3 style={howStyles.stepTitle}>
            Connect
          </h3>

          <p style={howStyles.description}>
            Your ClusterClear page opens instantly
            with everything you want to share.
          </p>

        </motion.div>

      </motion.div>


      {/* BOTTOM STATEMENT */}
      <motion.div
        style={howStyles.bottom}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.5,
        }}
        variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.15,
              delayChildren: 0.2,
            },
          },
        }}
      >

        <motion.span variants={fadeUp}>
          Tap.
        </motion.span>

        <motion.span variants={fadeUp}>
          Connect.
        </motion.span>

        <motion.span variants={fadeUp}>
          Grow.
        </motion.span>

      </motion.div>

    </section>
  );
};


const howStyles = {

  container: {
    background: "#f8f8f8",
    color: "#111",
    padding: "140px 20px",
    textAlign: "center",
  },

  header: {
    maxWidth: "700px",
    margin: "0 auto",
  },

  eyebrow: {
    fontSize: "11px",
    letterSpacing: "0.18em",
    fontWeight: "600",
    color: "#888",
    marginBottom: "22px",
  },

  title: {
    fontSize: "clamp(3rem, 6vw, 5rem)",
    lineHeight: "1",
    letterSpacing: "-0.055em",
    fontWeight: "700",
    margin: 0,
  },

  titleLight: {
    color: "#888",
    fontWeight: "600",
  },

  intro: {
    maxWidth: "560px",
    margin: "28px auto 0",
    fontSize: "1.1rem",
    lineHeight: "1.6",
    color: "#666",
  },

  steps: {
    maxWidth: "1050px",
    margin: "90px auto 0",
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "70px",
  },

  step: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },

  number: {
    fontSize: "12px",
    color: "#999",
    letterSpacing: "0.1em",
    marginBottom: "25px",
    fontWeight: "600",
  },

  iconWrapper: {
    width: "68px",
    height: "68px",
    borderRadius: "50%",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "25px",
    boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
  },

  stepTitle: {
    fontSize: "1.35rem",
    fontWeight: "600",
    margin: "0 0 12px",
    letterSpacing: "-0.02em",
  },

  description: {
    maxWidth: "260px",
    margin: 0,
    fontSize: "0.95rem",
    lineHeight: "1.6",
    color: "#777",
  },

  bottom: {
    marginTop: "100px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "18px",
    fontSize: "0.9rem",
    color: "#888",
  },
};

export default HowItWorks;